/**
 * SOIRÉES — LA CAISSE DE LA NUIT, CE QUE L'ÉCRAN EN ÉCRIT (15/10/2026).
 *
 * Module PUR (ni React ni réseau) : les phrases des quatre emplacements —
 * fiche de la soirée, cartes de la semaine, liste, calendrier — à partir de
 * la réponse de `GET /api/v1/soirees/night-cash`. Les CHIFFRES viennent du
 * serveur, calculés comme la fiche de nuit les calcule (`caisseDeLaNuit`) :
 * ici, aucune addition, seulement des mots.
 *
 * Trois règles d'écriture :
 *   · ce sont des chiffres de NUIT : quand deux soirées partagent une nuit,
 *     on le dit (« nuit du 03/10 · 2 soirées ») — jamais « le CA de la
 *     soirée » ;
 *   · un rapport qui manque n'est pas un zéro, un comptage qui n'a pas eu
 *     lieu non plus : la ligne le dit en commençant par l'état (« Pas encore
 *     de rapport », « Pas encore compté ») — jamais « Compté pas encore »,
 *     qui se lisait à l'envers (19/10/2026, demande d'Hugo) ;
 *   · avant la première nuit dont Gestion a une trace de caisse, il n'y aura
 *     jamais de rapport : les listes se taisent (la fiche le dit en une phrase).
 */

export type EmplacementCaisse = 'soiree_page' | 'week_cards' | 'history' | 'calendar'

export interface NuitCaisse {
  business_date: string
  has_data: boolean
  soirees: Array<{ id: string; name: string; status: string }>
  rapport_cents: number | null
  /** `HORS_GESTION` : nuit reprise de l'historique — rien n'est « attendu ». */
  rapport_state: 'RECUS' | 'EN_ATTENTE' | 'NON_LISIBLE' | 'HORS_GESTION'
  rapport_help: string
  compte_cents: number | null
  compte_state: 'COMPTE' | 'EN_COURS' | 'PAS_ENCORE' | 'HORS_GESTION'
  compte_help: string
  ca_arrete_cents: number | null
  ca_arrete_base: 'arrete' | 'valide' | null
  ca_arrete_help: string | null
}

export interface ReponseCaisse {
  /** Faux : pas le droit, ou emplacement éteint dans les Réglages — rien ne s'affiche. */
  visible: boolean
  show_closed_revenue: boolean
  current_night: string
  coverage_from: string | null
  soirees: Array<{ id: string; night: string }>
  nights: NuitCaisse[]
}

/** Garde minimale : un corps d'erreur (403, 422…) n'est pas une réponse. */
export function commeReponseCaisse(v: unknown): ReponseCaisse | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  if (typeof o['visible'] !== 'boolean' || !Array.isArray(o['nights']) || !Array.isArray(o['soirees'])) return null
  return o as unknown as ReponseCaisse
}

/** La nuit d'une soirée, telle que le serveur l'a tranchée (coupure de 8 h). */
export function nuitDeLaSoiree(r: ReponseCaisse | null, soireeId: string): NuitCaisse | null {
  if (!r?.visible) return null
  const nuit = r.soirees.find((s) => s.id === soireeId)?.night
  return nuit ? r.nights.find((n) => n.business_date === nuit) ?? null : null
}

/**
 * Ce que l'emplacement fait de cette nuit :
 *   · `rien`   — nuit en cours ou à venir, ou d'avant la première trace de
 *     caisse : une liste se tait ;
 *   · `avant`  — d'avant la première trace : la fiche le dit en une phrase ;
 *   · `chiffres` — Rapports, Compté (et le CA arrêté s'il existe).
 */
export function etatAffichage(r: ReponseCaisse, n: NuitCaisse): 'rien' | 'avant' | 'chiffres' {
  if (n.has_data) return 'chiffres'
  if (r.coverage_from === null || n.business_date < r.coverage_from) return 'avant'
  if (n.business_date >= r.current_night) return 'rien'
  return 'chiffres'
}

const FMT_ENTIER = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
const FMT = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** « 26 317 € » (listes, calendrier) ou « 26 317,00 € » (fiche) — la forme de `formatMoney`. */
export function euros(cents: number, auCentime = false): string {
  // Espace INSÉCABLE avant « € » (les milliers le sont déjà, U+202F) : un montant ne se coupe jamais en deux lignes.
  return auCentime ? `${FMT.format(cents / 100)}\u00a0€` : `${FMT_ENTIER.format(Math.round(cents / 100))}\u00a0€`
}

/** « 03/10 ». */
export function jourMoisCourt(date: string): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}`
}

export interface TextesCaisse {
  /**
   * Valeurs COURTES de la tuile de la fiche (liste libellé/valeur, le
   * libellé est dans sa propre colonne : « Rapports : pas encore reçus »).
   * « 26 317 € », « pas encore reçus », « montant réservé », « hors Gestion ».
   */
  rapports: string
  compte: string
  /** « 27 868 € » quand la nuit a un CA arrêté ou validé (et que les Réglages le montrent), sinon `null`. */
  caArrete: string | null
  /**
   * « CA validé » quand le chiffre vient d'une validation (toutes celles
   * d'avant le 16/10 sont des reprises de la DAF), « CA arrêté » quand il
   * vient d'une clôture : validé ≠ arrêté (doctrine de `feuille-de-caisse.ts`).
   */
  caLibelle: 'CA arrêté' | 'CA validé'
  /**
   * Les segments de la ligne des listes, chacun une phrase autonome : un
   * montant connu s'écrit « Libellé montant », un montant absent se dit par
   * son état, en tête (« Pas encore compté »).
   */
  segments: string[]
  /** « nuit du 03/10 · 2 soirées » quand la nuit en porte plusieurs ; `null` sinon. */
  nuitPartagee: string | null
  /** Le détail, pour une bulle d'aide : les aides de la fiche de nuit. */
  aide: string
  /** Les montants sont-ils là (pour l'accessibilité et les tests) ? */
  rapportsConnus: boolean
  compteConnu: boolean
}

export function textesCaisse(r: ReponseCaisse, n: NuitCaisse, opts: { auCentime?: boolean } = {}): TextesCaisse {
  const c = opts.auCentime === true
  // Une nuit reprise de l'historique n'a ni rapport ni comptage dans Gestion,
  // et n'en aura jamais : « pas encore » promettrait une arrivée. Son chiffre
  // est le CA validé, juste à côté. « montant réservé » : le mot que la
  // Comptabilité emploie déjà pour un montant caché par les droits.
  const rapports = n.rapport_cents !== null ? euros(n.rapport_cents, c)
    : n.rapport_state === 'HORS_GESTION' ? 'hors Gestion'
      : n.rapport_state === 'NON_LISIBLE' ? 'montant réservé' : 'pas encore reçus'
  const compte = n.compte_cents === null ? (n.compte_state === 'HORS_GESTION' ? 'hors Gestion' : 'pas encore')
    : n.compte_state === 'EN_COURS' ? `${euros(n.compte_cents, c)} (en cours)` : euros(n.compte_cents, c)
  const caArrete = r.show_closed_revenue && n.ca_arrete_cents !== null ? euros(n.ca_arrete_cents, c) : null
  const caLibelle = n.ca_arrete_base === 'valide' ? 'CA validé' : 'CA arrêté'

  // La ligne des listes se construit sur les ÉTATS, pas en collant « Compté »
  // devant une valeur courte : « Compté pas encore » se lisait à l'envers.
  const reprise = n.rapport_state === 'HORS_GESTION' && n.compte_state === 'HORS_GESTION'
  const rienDuTout = n.rapport_cents === null && n.rapport_state === 'EN_ATTENTE'
    && n.compte_cents === null && n.compte_state === 'PAS_ENCORE'
  const segments = reprise ? ['Repris de l’historique']
    : rienDuTout ? ['Pas encore de rapport ni de comptage']
      : [
          n.rapport_cents !== null ? `Rapports ${rapports}`
            : n.rapport_state === 'NON_LISIBLE' ? 'Rapports (montant réservé)'
              : n.rapport_state === 'HORS_GESTION' ? 'Rapports hors Gestion' : 'Pas encore de rapport',
          n.compte_cents !== null ? `Compté ${compte}`
            : n.compte_state === 'HORS_GESTION' ? 'Comptage hors Gestion' : 'Pas encore compté',
        ]
  if (caArrete !== null) segments.push(`${caLibelle} ${caArrete}`)

  const maintenues = n.soirees.filter((s) => s.status !== 'CANCELLED').length
  const nuitPartagee = maintenues > 1 ? `nuit du ${jourMoisCourt(n.business_date)} · ${maintenues} soirées` : null
  const aide = [
    `Chiffres de la nuit du ${jourMoisCourt(n.business_date)}${maintenues > 1 ? `, communs à ses ${maintenues} soirées` : ''}.`,
    `Rapports : ${n.rapport_help}.`,
    `Compté : ${n.compte_help}.`,
    ...(caArrete !== null && n.ca_arrete_help ? [`${caLibelle} : ${n.ca_arrete_help}.`] : []),
  ].join(' ')
  return {
    rapports, compte, caArrete, caLibelle, segments, nuitPartagee, aide,
    rapportsConnus: n.rapport_cents !== null, compteConnu: n.compte_cents !== null,
  }
}

/**
 * LA ligne de caisse des listes — l'unique constructeur des cinq emplacements
 * (liste, cartes de la semaine, lignes du calendrier, bulle, fiche rapide) :
 * « Rapports 26 317 € · Compté 26 278 € · CA arrêté 27 868 € »,
 * « Rapports 33 803 € · Pas encore compté », « Repris de l’historique · CA validé … ».
 */
export function ligneCaisse(t: TextesCaisse): string {
  return t.segments.join(' · ')
}

/** Les dates de l'écran → la plage demandée au serveur (une seule requête). */
export function plageDesDates(dates: string[]): { from: string; to: string } | null {
  const ds = dates.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)).sort()
  return ds.length === 0 ? null : { from: ds[0]!, to: ds[ds.length - 1]! }
}

/** La date (Europe/Paris) d'un instant ISO : « 2026-10-03 ». */
export function dateParis(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' })
}

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

/** « 3 septembre 2026 », « 1er septembre 2016 » d'une date AAAA-MM-JJ — sans fuseau : c'est déjà un jour. */
export function jourMoisAnnee(date: string): string {
  const m = MOIS[Number(date.slice(5, 7)) - 1]
  const j = Number(date.slice(8, 10))
  // Le premier du mois s'écrit « 1er » en français, jamais « 1 ».
  return m === undefined ? date : `${j === 1 ? '1er' : j} ${m} ${date.slice(0, 4)}`
}

/**
 * La phrase de la fiche pour une nuit d'avant la première trace de caisse.
 *
 * L'ANNÉE EST DITE. « depuis le 03/09 », lu sur une soirée du 10/10/2025,
 * se comprenait « 03/09/2025 » — avant la soirée : la phrase semblait
 * fausse. Et sans aucune trace dans Gestion, il n'y a pas de date du tout :
 * la phrase ne finit plus sur « le — ».
 */
export function phraseAvantCouverture(coverageFrom: string | null): string {
  return coverageFrom === null
    ? 'Pas de rapport de caisse pour cette nuit : Gestion n’en reçoit pas encore.'
    : `Pas de rapport de caisse dans Gestion pour cette nuit : ils n’y arrivent que depuis le ${jourMoisAnnee(coverageFrom)}.`
}

/**
 * La tuile de la fiche d'une soirée n'a-t-elle rien à montrer pour CETTE
 * soirée ? Décidé sur la nuit de la soirée, pas sur « une réponse est là » :
 * en passant d'une soirée à une autre, la réponse de la précédente ne
 * nomme pas la nouvelle — la tuile se retire au lieu d'afficher son
 * remplissage d'édition (« Rien à afficher ici pour l'instant »).
 */
export function tuileCaisseAbsente(r: ReponseCaisse | null, soireeId: string): boolean {
  return nuitDeLaSoiree(r, soireeId) === null
}

/* ═══════════════════════ l'historique, mois par mois ═══════════════════════ */

export interface PlageCaisse { from: string; to: string }

/**
 * Le mois civil d'une date (« 2026-10-03 » → du 01/10 au 31/10) : la plage
 * qu'un mois de l'historique demande au serveur. Le mois ENTIER, pas les
 * dates de ses lignes : une recherche ou un changement de période qui
 * retire des lignes retombe sur la même plage, donc sur la lecture déjà
 * faite — taper dans la recherche ne relance aucun calcul.
 */
export function plageDuMois(date: string): PlageCaisse | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  const a = Number(date.slice(0, 4))
  const m = Number(date.slice(5, 7))
  if (m < 1 || m > 12) return null
  const dernier = new Date(Date.UTC(a, m, 0)).getUTCDate()
  return { from: `${date.slice(0, 7)}-01`, to: `${date.slice(0, 7)}-${String(dernier).padStart(2, '0')}` }
}

export const clePlage = (p: PlageCaisse): string => `${p.from}_${p.to}`

/**
 * LES LECTURES D'UNE SECTION, UNE À LA FOIS, CHACUNE UNE SEULE FOIS.
 *
 * Pourquoi : chaque nuit qui a une trace de caisse coûte au serveur la
 * fiche de nuit ENTIÈRE (une trentaine de requêtes). L'historique demandait
 * toute sa période d'un coup — une saison, et « toutes les saisons »
 * remontait à 2020 : des milliers de requêtes à chaque visite, sur le pool
 * que partage la pointeuse. Désormais chaque mois ne se demande que quand il
 * PARAÎT à l'écran, une seule fois pour la vie de la page, et les mois
 * demandés ensemble passent l'un après l'autre (jamais deux calculs de
 * l'historique de front).
 *
 * Module pur : `lire` (le réseau) et `avertir` (le rendu) sont fournis.
 */
export class LecturesCaisseParPlage {
  private readonly faites = new Map<string, ReponseCaisse | null>()
  private readonly attente: PlageCaisse[] = []
  private enCours: string | null = null
  private arretee = false

  constructor(
    private readonly lire: (p: PlageCaisse) => Promise<ReponseCaisse | null>,
    private readonly avertir: () => void,
  ) {}

  /** La réponse d'une plage déjà lue ; `null` tant qu'elle ne l'est pas (ou rien à montrer). */
  reponse(p: PlageCaisse | null): ReponseCaisse | null {
    return p === null ? null : this.faites.get(clePlage(p)) ?? null
  }

  /** Demande une plage : sans effet si elle est lue, en cours ou déjà en file. */
  demander(p: PlageCaisse | null): void {
    if (p === null || this.arretee) return
    const k = clePlage(p)
    if (this.faites.has(k) || this.enCours === k || this.attente.some((x) => clePlage(x) === k)) return
    this.attente.push(p)
    this.pomper()
  }

  /** La page part : la file se vide, une réponse tardive n'est plus rendue. */
  arreter(): void {
    this.arretee = true
    this.attente.length = 0
  }

  /** Nombre de lectures demandées et pas encore servies (en cours compris). */
  get enAttente(): number {
    return this.attente.length + (this.enCours === null ? 0 : 1)
  }

  private pomper(): void {
    if (this.enCours !== null || this.arretee) return
    const p = this.attente.shift()
    if (p === undefined) return
    const k = clePlage(p)
    this.enCours = k
    void this.lire(p)
      .then((r) => r, () => null)
      .then((r) => {
        this.enCours = null
        if (this.arretee) return
        this.faites.set(k, r?.visible ? r : null)
        this.avertir()
        this.pomper()
      })
  }
}
